import { useCallback, useContext, useRef } from 'react';

import type { OutreachEditorRef } from '@recruit-robin/robin-theme/web-components';

import { useTranslateMessageMutation } from 'hooks/shared/useTranslateMessageMutation';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';

export type TranslationSourceContent = {
  subject?: string;
  message?: string;
  /** Language the message was generated in, as reported by the generation endpoint */
  language?: string;
};

type TranslateEditorMessageParams = {
  refs: { subject: OutreachEditorRef | null; message: OutreachEditorRef | null };
  /** Message as it came back from the generation endpoint — the source every translation starts from */
  source?: TranslationSourceContent;
  onTranslated?: (message: string) => void;
};

type EditorContent = { subject: string; message: string };

/**
 * @deprecated Use `useTranslateEditorMessage` from `@/shared/lib/translate-message` instead.
 * This legacy copy is kept only for the callers in `src/components/ui/organisms/`; FSD code must
 * not import from `src/hooks/`. Remove this file once those callers point at the shared copy.
 *
 * Translates the generated outreach message instead of regenerating it.
 *
 * Every translation starts from the same source — the message returned by the generation
 * endpoint — so switching between languages never translates a translation and always reuses the
 * cached result of `useTranslateMessageMutation`. That source is also what gets restored when the
 * user goes back to the automatic language, or picks the language the message was generated in.
 */
export const useTranslateEditorMessage = ({ refs, source, onTranslated }: TranslateEditorMessageParams) => {
  const { language } = useContext(LocalizationContext);
  const translateMessageMutation = useTranslateMessageMutation();
  const editorContentFallbackRef = useRef<EditorContent | null>(null);
  const translationsRef = useRef<Record<string, EditorContent>>({});
  const translationsSourceRef = useRef<string | null>(null);
  const appliedTranslationRef = useRef<string | null>(null);

  const resolveSourceContent = useCallback((): EditorContent | null => {
    const resolve = (): EditorContent | null => {
      if (source?.message) {
        return { subject: source.subject ?? '', message: source.message };
      }

      // No generated content available (e.g. the message was typed by the user) — fall back to what
      // the editor held before the first translation.
      if (!editorContentFallbackRef.current) {
        const message = refs.message?.serializeToHTML();
        if (!message) return null;

        editorContentFallbackRef.current = { subject: refs.subject?.getTextContent() ?? '', message };
      }

      return editorContentFallbackRef.current;
    };

    const sourceContent = resolve();

    // A newly generated message invalidates the translations made from the previous one
    if (sourceContent && translationsSourceRef.current !== sourceContent.message) {
      translationsSourceRef.current = sourceContent.message;
      translationsRef.current = {};
      appliedTranslationRef.current = null;
    }

    return sourceContent;
  }, [source?.subject, source?.message, refs.subject, refs.message]);

  const applyEditorContent = useCallback(
    ({ subject, message }: EditorContent) => {
      if (subject) refs.subject?.importFromHTML(subject, language);
      refs.message?.importFromHTML(message, language);
      onTranslated?.(message);
    },
    [refs.subject, refs.message, language, onTranslated],
  );

  const restoreOriginalContent = useCallback(() => {
    const sourceContent = resolveSourceContent();
    if (!sourceContent) return false;

    if (appliedTranslationRef.current) {
      applyEditorContent(sourceContent);
      appliedTranslationRef.current = null;
    }

    return true;
  }, [resolveSourceContent, applyEditorContent]);

  const translateEditorContent = useCallback(
    async (targetLanguage: string) => {
      const sourceContent = resolveSourceContent();
      if (!sourceContent) return false;

      // The message was already generated in this language, so there is nothing to translate
      if (source?.language && source.language.toLowerCase() === targetLanguage.toLowerCase()) {
        return restoreOriginalContent();
      }

      if (appliedTranslationRef.current === targetLanguage) return true;

      const alreadyTranslated = translationsRef.current[targetLanguage];
      if (alreadyTranslated) {
        applyEditorContent(alreadyTranslated);
        appliedTranslationRef.current = targetLanguage;
        return true;
      }

      try {
        const { translatedMessage, translatedSubject } = await translateMessageMutation.mutateAsync({
          message: sourceContent.message,
          targetLanguage,
          subject: sourceContent.subject || undefined,
        });

        const translation = { subject: translatedSubject ?? sourceContent.subject, message: translatedMessage };
        translationsRef.current[targetLanguage] = translation;
        applyEditorContent(translation);
        appliedTranslationRef.current = targetLanguage;

        return true;
      } catch {
        // the mutation already reports the failure to the user
        return false;
      }
    },
    [resolveSourceContent, applyEditorContent, restoreOriginalContent, translateMessageMutation, source?.language],
  );

  const resetOriginalContent = useCallback(() => {
    editorContentFallbackRef.current = null;
    translationsSourceRef.current = null;
    translationsRef.current = {};
    appliedTranslationRef.current = null;
  }, []);

  return {
    translateEditorContent,
    restoreOriginalContent,
    resetOriginalContent,
    isTranslating: translateMessageMutation.isLoading,
  };
};
